import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Receipt, Plus, Search, Pencil, Trash2, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'toll', label: 'Toll Fees' },
  { value: 'salary', label: 'Salaries' },
  { value: 'office', label: 'Office' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];

const vatTypes = [
  { value: 'inclusive', label: 'VAT Inclusive (15%)' },
  { value: 'exclusive', label: 'VAT Exclusive (add 15%)' },
  { value: 'no_vat', label: 'No VAT' },
];

const categoryColors = {
  fuel: 'bg-amber-100 text-amber-700',
  maintenance: 'bg-blue-100 text-blue-700',
  insurance: 'bg-purple-100 text-purple-700',
  toll: 'bg-green-100 text-green-700',
  salary: 'bg-pink-100 text-pink-700',
  office: 'bg-cyan-100 text-cyan-700',
  vehicle: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-700'
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [financialYears, setFinancialYears] = useState([]);
  const [filterYear, setFilterYear] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: 'other',
    vat_type: 'inclusive',
    vehicle_registration: '',
    notes: ''
  });

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [filterCategory, filterYear]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      setFinancialYears(response.data.financial_years || []);
    } catch (error) {
      console.error('Failed to load financial years');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterYear !== 'all') params.financial_year = filterYear;
      const response = await axios.get(`${API_URL}/finance/expenses`, { params });
      setExpenses(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (editingExpense) {
        await axios.put(`${API_URL}/finance/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API_URL}/finance/expenses`, payload);
        toast.success('Expense added');
      }

      setShowDialog(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save expense'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!confirm(`Delete expense "${expense.description}"?`)) return;

    try {
      await axios.delete(`${API_URL}/finance/expenses/${expense.id}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete expense'));
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setForm({
      date: expense.date,
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category,
      vat_type: expense.vat_type || 'inclusive',
      vehicle_registration: expense.vehicle_registration || '',
      notes: expense.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      category: 'other',
      vat_type: 'inclusive',
      vehicle_registration: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const filteredExpenses = expenses.filter(exp =>
    exp.description?.toLowerCase().includes(search.toLowerCase()) ||
    exp.vehicle_registration?.toLowerCase().includes(search.toLowerCase())
  );

  const totalNetExclVAT = filteredExpenses.reduce((sum, exp) => sum + (exp.net_excl_vat || exp.amount || 0), 0);
  const totalVAT = filteredExpenses.reduce((sum, exp) => sum + (exp.vat_amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="expenses-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Expenses</h1>
            <p className="text-muted-foreground mt-1">Manual expense capture with VAT tracking</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-expense-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Financial Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {financialYears.map(fy => (
                    <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">Net Excl VAT</p>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(totalNetExclVAT)}</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">VAT Amount</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalVAT)}</p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Total Incl VAT</p>
            <p className="text-2xl font-bold">{formatCurrency(totalNetExclVAT + totalVAT)}</p>
          </div>
        </div>

        {/* Expenses Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : filteredExpenses.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No expenses found</h3>
                <p className="text-muted-foreground mb-4">Start by adding your first expense</p>
                <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>VAT Type</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Net Excl VAT</TableHead>
                    <TableHead className="text-right">VAT</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell className="text-muted-foreground">
                        {new Date(expense.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{expense.description}</TableCell>
                      <TableCell>
                        <Badge className={categoryColors[expense.category] || categoryColors.other}>
                          {categories.find(c => c.value === expense.category)?.label || expense.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {vatTypes.find(v => v.value === expense.vat_type)?.label || expense.vat_type || 'Inclusive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{expense.vehicle_registration || '-'}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        {formatCurrency(expense.net_excl_vat || expense.amount)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(expense.vat_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(expense)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
              <DialogDescription>
                {editingExpense ? 'Update the expense details' : 'Enter the expense details'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (R) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="e.g., Diesel refill at Engen"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>VAT Type *</Label>
                    <Select value={form.vat_type} onValueChange={(value) => setForm({ ...form, vat_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {vatTypes.map(vat => (
                          <SelectItem key={vat.value} value={vat.value}>{vat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicle">Vehicle Registration</Label>
                  <Input
                    id="vehicle"
                    value={form.vehicle_registration}
                    onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value })}
                    placeholder="e.g., CA 123-456"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingExpense ? 'Update' : 'Add Expense')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
